import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, ProgressBar } from "react-bootstrap";
import { PROJECT_STATUS_META, statusMeta, formatDateOrDash } from "./projectWorkspaceUtils";
import { AuthContext } from "../../context/AuthContext";
import Can from "../../components/Can";

// "Manage Members & Modules" opens ProjectManagementPage's inline
// ?tab=members panel (ProjectDetailPanel.jsx), which itself hosts THREE
// independently-authorized sub-resources -- project members, project
// modules, and module members -- each already gated action-by-action via
// its own <Can>/can() calls in ProjectDetailPanel.jsx, ProjectModuleManagement.jsx
// and ModuleMembersManagement.jsx (unchanged by this fix). These are the
// REAL, already-seeded permission modules those pages/routes use (see
// routes/projectRoutes.js's /members routes and routes/projectModuleRoutes.js
// -- database/phase-1b/005_seed_project_permissions.sql defines exactly
// these 4 modules: projects, project_modules, project_members,
// project_module_members) -- no new permission is invented here.
//
// The button itself is gated by the MINIMUM SAFE RULE: show it only when
// the user holds at least one non-"view" (i.e. genuinely management)
// action across those three membership/module modules -- "view" alone
// would never make this destination any more useful than what
// projects:view (already required just to reach this page) provides.
// Deliberately EXCLUDES the plain 'projects' module's own edit/delete/etc.
// (that's Project Management itself, a separate concern from members/
// modules) and excludes every '*:view' action for the same reason.
// A user with e.g. ONLY project_modules:create (Developer's actual grant,
// database/phase-17-developer-project-self-service/001_...sql) legitimately
// sees the button and can create a module -- every OTHER action on that
// page (add/remove members, edit/delete modules, etc.) stays hidden via
// its own existing <Can>, exactly per "individual actions inside that page
// must continue to be independently permission-gated."
const CAN_MANAGE_PROJECT_MEMBERSHIP_OR_MODULES = [
  { moduleKey: "project_members", actionKey: "add" },
  { moduleKey: "project_members", actionKey: "edit" },
  { moduleKey: "project_members", actionKey: "remove" },
  { moduleKey: "project_members", actionKey: "manage" },
  { moduleKey: "project_modules", actionKey: "create" },
  { moduleKey: "project_modules", actionKey: "edit" },
  { moduleKey: "project_modules", actionKey: "delete" },
  { moduleKey: "project_modules", actionKey: "activate" },
  { moduleKey: "project_modules", actionKey: "deactivate" },
  { moduleKey: "project_modules", actionKey: "change_owner" },
  { moduleKey: "project_module_members", actionKey: "add" },
  { moduleKey: "project_module_members", actionKey: "edit" },
  { moduleKey: "project_module_members", actionKey: "remove" },
  { moduleKey: "project_module_members", actionKey: "manage" },
];

// Compact workspace header: name/type/code/owner/dates/overall progress/
// current phase/status badge/member count -- shared by every tab since it
// sits above the <Tabs>. Sourced entirely from GET /projects/:id/overview
// (see ProjectWorkspacePage.jsx), fetched once for the whole page rather
// than per tab.
//
// This same component is reached from more than one role-prefixed route
// (/admin/projects/:projectId AND /developer/projects/:projectId, see
// routes/DynamicRoutes.jsx's shared 'projects/:projectId' entry) -- its own
// "back"/"manage" navigation must follow the CURRENT user's role prefix
// rather than being hardcoded to /admin/..., same convention as
// pages/tasks/TaskDetailPage.jsx's `basePath`. Never assume /admin here.
// Phase 90: `finalPlan` (the GET /projects/:id/final-plan payload) is passed
// only while the final-plan view is selected; the header then reports the
// M1-M8 plan -- milestone status rollup, current milestone and screen
// progress -- so it never contradicts the tab below it.
function finalPlanSummaryParts(statusCounts) {
  const order = [
    ["DELAYED", "delayed"], ["BLOCKED", "blocked"], ["AT_RISK", "at risk"], ["IN_PROGRESS", "in progress"],
    ["READY_TO_COMPLETE", "ready to complete"], ["COMPLETED", "completed"], ["COMPLETED_LATE", "completed late"],
    ["NOT_STARTED", "not started"],
  ];
  return order.filter(([key]) => statusCounts[key] > 0).map(([key, label]) => `${statusCounts[key]} ${label}`);
}

export default function ProjectWorkspaceHeader({ projectId, overview, loading, error, onRefresh, finalPlan }) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const basePath = `/${user?.role || "admin"}`;
  const project = overview?.project;
  const meta = statusMeta(PROJECT_STATUS_META, overview?.projectStatus);
  const planSummary = finalPlan?.available ? finalPlan.summary : null;
  const currentMilestone = planSummary?.current_milestone;
  const progressValue = planSummary ? planSummary.screens.progress_percent : overview?.overallProgress;
  // Task 6: compact "1 delayed · 1 in progress · 1 completed · 8 upcoming"
  // summary, purely a read-only rollup of the already-derived phase
  // statuses (see phaseStatusSummary in services/projectWorkspaceService.js's
  // getOverview()) -- never a replacement for the badge above, which stays
  // the project's own stored lifecycle/timeline-health status.
  const summary = overview?.phaseStatusSummary;
  const summaryParts = planSummary
    ? finalPlanSummaryParts(planSummary.milestone_status_counts || {})
    : summary
    ? [
        summary.delayed > 0 && `${summary.delayed} delayed`,
        summary.blocked > 0 && `${summary.blocked} blocked`,
        summary.at_risk > 0 && `${summary.at_risk} at risk`,
        summary.in_progress > 0 && `${summary.in_progress} in progress`,
        summary.completed > 0 && `${summary.completed} completed`,
        summary.not_started > 0 && `${summary.not_started} upcoming`,
      ].filter(Boolean)
    : [];

  return (
    <div className="pw-header">
      <button type="button" className="pw-back-link" onClick={() => navigate(`${basePath}/projects`)}>
        <i className="fa-solid fa-arrow-left" aria-hidden="true"></i> Back to Projects
      </button>

      {loading ? (
        <div className="text-muted small">Loading project…</div>
      ) : error ? (
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-danger small">{error}</div>
          <button type="button" className="unit-btn unit-btn-light" onClick={onRefresh}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i> Retry
          </button>
        </div>
      ) : !project ? (
        <div className="text-muted small">Project not found.</div>
      ) : (
        <>
          <div className="pw-header-top">
            <div className="pw-header-title-row">
              <h2>{project.name}</h2>
              {project.code && <span className="pw-chip pw-chip-secondary"><i className="fa-solid fa-code" aria-hidden="true"></i>{project.code}</span>}
              {project.project_type_name && (
                <span className="pw-chip"><i className="fa-solid fa-diagram-project" aria-hidden="true"></i>{project.project_type_name}</span>
              )}
              <Badge bg={meta.tone}>
                <i className={`fa-solid ${meta.icon || "fa-circle"} me-1`} aria-hidden="true"></i>
                {meta.label}
              </Badge>
              {summaryParts.length > 0 && (
                <span className="pw-phase-status-summary text-muted small">{summaryParts.join(" · ")}</span>
              )}
            </div>

            <div className="pw-header-actions">
              <button type="button" className="unit-btn unit-btn-light" onClick={onRefresh}>
                <i className="fa-solid fa-rotate" aria-hidden="true"></i> Refresh
              </button>
              <Can any={CAN_MANAGE_PROJECT_MEMBERSHIP_OR_MODULES}>
                <button
                  type="button"
                  className="unit-btn unit-btn-light"
                  onClick={() => navigate(`${basePath}/projects?project=${projectId}&tab=members`)}
                  title="Manage members and modules for this project"
                >
                  <i className="fa-solid fa-users-gear" aria-hidden="true"></i> Manage Members &amp; Modules
                </button>
              </Can>
            </div>
          </div>

          <div className="pw-header-meta">
            <div className="pw-meta-item">
              <span>Owner</span>
              <strong>{project.owner_name || "Unassigned"}</strong>
            </div>
            <div className="pw-meta-item">
              <span>Department</span>
              <strong>{project.department_name || "—"}</strong>
            </div>
            <div className="pw-meta-item">
              <span>Unit</span>
              <strong>{project.unit_name || "Global"}</strong>
            </div>
            <div className="pw-meta-item">
              <span>Start Date</span>
              <strong>{formatDateOrDash(project.start_date)}</strong>
            </div>
            <div className="pw-meta-item">
              <span>Target End</span>
              <strong>{formatDateOrDash(project.end_date)}</strong>
            </div>
            {planSummary ? (
              <div className="pw-meta-item">
                <span>Current Milestone</span>
                <strong title={currentMilestone?.name}>
                  {currentMilestone ? `${currentMilestone.code} · ${currentMilestone.status_label}` : "—"}
                </strong>
              </div>
            ) : (
              <div className="pw-meta-item">
                <span>Current Phase</span>
                <strong>{overview?.currentPhase?.name || "—"}</strong>
              </div>
            )}
            <div className="pw-meta-item">
              <span>Team</span>
              <strong>
                <i className="fa-solid fa-users me-1" aria-hidden="true"></i>
                {project.active_member_count ?? 0} member{(project.active_member_count ?? 0) === 1 ? "" : "s"}
              </strong>
            </div>

            <div className="pw-progress-wrap">
              <span className="d-block mb-1" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", color: "#8b877f", fontWeight: 700 }}>
                {planSummary ? "Final Plan Progress (screens)" : "Overall Progress"}
              </span>
              {progressValue === null || progressValue === undefined ? (
                <span className="text-muted small">No tasks added yet</span>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <ProgressBar now={progressValue} style={{ flex: 1 }} />
                  <strong style={{ fontSize: 13 }}>{Math.round(progressValue)}%</strong>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
